/**
 * PostIssueModal — Kiállítás utáni felugró: kiküldés / nyomtatás / bezárás
 * Billentyűk: F6 = kiküldés, F8 = nyomtatás, Escape = bezárás
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { get, post, downloadFile } from '../lib/api';
import { Modal, Button, Badge, LoadingOverlay } from './ui';
import { useToast } from './ui/Toast';

interface Contact {
  id: string;
  name: string;
  email: string;
  is_primary?: boolean;
  is_billing?: boolean;
}

interface PostIssueModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** 'invoice' | 'proforma' | 'delivery-note' */
  docType: 'invoice' | 'proforma' | 'delivery-note';
  /** Dokumentum ID */
  docId: string;
  /** Dokumentum sorszám (megjelenítéshez) */
  docNumber: string;
  /** Partner ID (kontaktok betöltéséhez) */
  partnerId: string;
  /** Partner neve */
  partnerName: string;
  /** Készpénzes fizetés — pénztárbizonylat csatolás kérdezése */
  isCash?: boolean;
  /** Kapcsolódó pénztárbizonylat ID (ha auto-generálva) */
  cashReceiptId?: string;
}

const DOC_LABELS: Record<string, string> = {
  invoice: 'Számla',
  proforma: 'Díjbekérő',
  'delivery-note': 'Szállítólevél',
};

type SendMode = 'billing' | 'select' | 'manual';

export default function PostIssueModal({ isOpen, onClose, docType, docId, docNumber, partnerId, partnerName, isCash, cashReceiptId }: PostIssueModalProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [sendMode, setSendMode] = useState<SendMode>('billing');
  const [billingEmail, setBillingEmail] = useState('');
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [selectedContactEmail, setSelectedContactEmail] = useState('');
  const [manualEmail, setManualEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [printing, setPrinting] = useState(false);
  const [includeCashReceipt, setIncludeCashReceipt] = useState(isCash ?? false);
  const [foundCashReceiptId, setFoundCashReceiptId] = useState(cashReceiptId || '');

  const label = DOC_LABELS[docType] || 'Dokumentum';
  const isInvoiceType = docType === 'invoice' || docType === 'proforma';
  const pdfUrl = isInvoiceType ? `/invoices/${docId}/pdf` : `/delivery-notes/${docId}/pdf`;

  // Email preview + kontaktok betöltése
  useEffect(() => {
    if (!isOpen) return;
    setSent(false);
    setSendMode('billing');
    setManualEmail('');
    setSelectedContactEmail('');
    setLoadingPreview(true);

    (async () => {
      // Email preview (billing fallback chain)
      if (isInvoiceType) {
        try {
          const preview = await get<any>(`/invoices/${docId}/email-preview`);
          setBillingEmail(preview?.toEmail || '');
        } catch { setBillingEmail(''); }
      } else {
        // Szállítólevélnél: partner emailjét és kontaktokat nézzük
        try {
          const partner = await get<any>(`/partners/${partnerId}`);
          setBillingEmail(partner?.email || '');
        } catch { setBillingEmail(''); }
      }

      // Készpénzes bizonylat keresése
      if (isCash && isInvoiceType && !cashReceiptId) {
        try {
          const receipts = await get<any>(`/cash-receipts?receiptType=BPB&status=issued`);
          const linked = (receipts.data || []).find((r: any) => r.invoice_id === docId);
          if (linked) setFoundCashReceiptId(linked.id);
        } catch { /* */ }
      }

      // Kontaktok betöltése
      try {
        const c = await get<any>(`/partners/${partnerId}/contacts`);
        const arr = Array.isArray(c) ? c : c?.data || [];
        setContacts(arr.filter((x: Contact) => x.email));
      } catch { setContacts([]); }

      setLoadingPreview(false);
    })();
  }, [isOpen, docId, partnerId]);

  // Billentyűkezelés
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!isOpen) return;
    if (e.key === 'F6') {
      e.preventDefault();
      handleSend();
    } else if (e.key === 'F8') {
      e.preventDefault();
      handlePrint();
    }
  }, [isOpen, sendMode, billingEmail, selectedContactEmail, manualEmail, sending, sent]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  function getTargetEmail(): string {
    if (sendMode === 'billing') return billingEmail;
    if (sendMode === 'select') return selectedContactEmail;
    if (sendMode === 'manual') return manualEmail;
    return '';
  }

  async function handleSend() {
    const email = getTargetEmail();
    if (!email || !email.includes('@')) {
      toast('Adj meg érvényes e-mail címet.', 'error');
      return;
    }
    if (sending) return;
    setSending(true);
    try {
      if (isInvoiceType) {
        await post(`/invoices/${docId}/send-email`, {
          toEmail: email,
          includeCashReceipt: includeCashReceipt && foundCashReceiptId ? foundCashReceiptId : undefined,
        });
      } else {
        toast('Szállítólevél email küldés fejlesztés alatt.', 'info');
        setSending(false);
        return;
      }
      setSent(true);
      toast(`${label} elküldve: ${email}${includeCashReceipt && foundCashReceiptId ? ' (pénztárbizonylattal)' : ''}`, 'success');
    } catch (e: any) {
      toast(e.message || 'Küldés sikertelen.', 'error');
    } finally {
      setSending(false);
    }
  }

  async function handlePrint() {
    setPrinting(true);
    try {
      const token = localStorage.getItem('accessToken');

      // Számla/díjbekérő PDF
      const res = await fetch(`/api${pdfUrl}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) { toast('PDF betöltés sikertelen.', 'error'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const printWindow = window.open(url);
      if (printWindow) {
        printWindow.addEventListener('load', () => { printWindow.print(); });
      }

      // Pénztárbizonylat PDF is, ha kérte
      if (includeCashReceipt && foundCashReceiptId) {
        const crRes = await fetch(`/api/cash-receipts/${foundCashReceiptId}/pdf`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (crRes.ok) {
          const crBlob = await crRes.blob();
          const crUrl = URL.createObjectURL(crBlob);
          const crWindow = window.open(crUrl);
          if (crWindow) {
            crWindow.addEventListener('load', () => { crWindow.print(); });
          }
        }
      }
    } catch {
      toast('Nyomtatás sikertelen.', 'error');
    } finally {
      setPrinting(false);
    }
  }

  if (!isOpen) return null;

  return (
    <>
    <LoadingOverlay visible={printing} text="PDF előkészítése..." />
    <LoadingOverlay visible={sending} text="E-mail küldése..." />
    <Modal isOpen={true} onClose={onClose} title={`${label} kiállítva`}>
      <div className="space-y-4">

        {/* Siker üzenet */}
        <div style={{ textAlign: 'center', padding: '12px 0' }}>
          <div style={{ fontSize: '36px', marginBottom: '8px' }}>
            {docType === 'delivery-note' ? '📦' : docType === 'proforma' ? '📋' : '✅'}
          </div>
          <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--navy)' }}>
            {label} sikeresen kiállítva
          </div>
          <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--ink)', marginTop: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            {docNumber}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--slate)', marginTop: '2px' }}>
            {partnerName}
          </div>
        </div>

        {/* Kiküldés szekció */}
        {isInvoiceType && (
          <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--r)', padding: '14px', background: 'var(--mist)' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--navy)', marginBottom: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>E-mail kiküldés</span>
              <kbd style={{ fontSize: '10px', background: 'var(--white)', border: '1px solid var(--line)', borderRadius: '3px', padding: '1px 6px', color: 'var(--slate)' }}>F6</kbd>
            </div>

            {loadingPreview ? (
              <div style={{ fontSize: '12px', color: 'var(--slate)' }}>Betöltés...</div>
            ) : (
              <>
                {/* Mód választás */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
                  {/* Számlázási kapcsolattartó (alapértelmezett) */}
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', padding: '6px 8px', borderRadius: 'var(--r)', background: sendMode === 'billing' ? 'var(--white)' : 'transparent', border: sendMode === 'billing' ? '1px solid var(--navy)' : '1px solid transparent' }}>
                    <input type="radio" name="sendMode" checked={sendMode === 'billing'} onChange={() => setSendMode('billing')}
                      style={{ accentColor: 'var(--navy)' }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, color: 'var(--navy)' }}>Számlázási kapcsolattartó</div>
                      <div style={{ fontSize: '11px', color: 'var(--slate)' }}>
                        {billingEmail ? billingEmail : 'Nincs beállítva e-mail cím'}
                      </div>
                    </div>
                    {billingEmail && <Badge variant="default" style={{ fontSize: '9px' }}>Alapértelmezett</Badge>}
                  </label>

                  {/* Kiválasztás a kontaktok közül */}
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer', fontSize: '13px', padding: '6px 8px', borderRadius: 'var(--r)', background: sendMode === 'select' ? 'var(--white)' : 'transparent', border: sendMode === 'select' ? '1px solid var(--navy)' : '1px solid transparent' }}>
                    <input type="radio" name="sendMode" checked={sendMode === 'select'} onChange={() => setSendMode('select')}
                      style={{ accentColor: 'var(--navy)', marginTop: '2px' }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, color: 'var(--navy)' }}>Kiválasztás</div>
                      {sendMode === 'select' && (
                        <div style={{ marginTop: '6px' }}>
                          {contacts.length === 0 ? (
                            <div style={{ fontSize: '11px', color: 'var(--slate)' }}>Nincs kapcsolattartó e-mail címmel.</div>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              {contacts.map(c => (
                                <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '12px', padding: '4px 6px', borderRadius: '4px', background: selectedContactEmail === c.email ? '#eff6ff' : 'transparent' }}>
                                  <input type="radio" name="contactEmail" checked={selectedContactEmail === c.email} onChange={() => setSelectedContactEmail(c.email)}
                                    style={{ accentColor: 'var(--navy)' }} />
                                  <span style={{ fontWeight: 500 }}>{c.name}</span>
                                  <span style={{ color: 'var(--slate)' }}>{c.email}</span>
                                  {c.is_billing && <Badge variant="info" style={{ fontSize: '8px' }}>Számlázási</Badge>}
                                  {c.is_primary && <Badge variant="default" style={{ fontSize: '8px' }}>Elsődleges</Badge>}
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </label>

                  {/* Kézi megadás */}
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer', fontSize: '13px', padding: '6px 8px', borderRadius: 'var(--r)', background: sendMode === 'manual' ? 'var(--white)' : 'transparent', border: sendMode === 'manual' ? '1px solid var(--navy)' : '1px solid transparent' }}>
                    <input type="radio" name="sendMode" checked={sendMode === 'manual'} onChange={() => setSendMode('manual')}
                      style={{ accentColor: 'var(--navy)', marginTop: '2px' }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, color: 'var(--navy)' }}>E-mail cím megadása</div>
                      {sendMode === 'manual' && (
                        <input type="email" value={manualEmail} onChange={e => setManualEmail(e.target.value)}
                          placeholder="pelda@email.hu" autoFocus
                          className="form-input" style={{ marginTop: '6px', fontSize: '13px' }}
                          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleSend(); } }}
                        />
                      )}
                    </div>
                  </label>
                </div>
              </>
            )}
          </div>
        )}

        {sent && (
          <div style={{ padding: '8px 12px', background: '#dcfce7', borderRadius: 'var(--r)', fontSize: '12px', color: '#166534', fontWeight: 600, textAlign: 'center' }}>
            Sikeresen elküldve!
          </div>
        )}

        {/* Készpénzes bizonylat csatolás kérdése */}
        {isCash && foundCashReceiptId && (
          <div style={{
            padding: '10px 14px', borderRadius: 'var(--r)', background: '#fefce8',
            border: '1px solid #fde68a', marginTop: '8px',
          }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '13px' }}>
              <input type="checkbox" checked={includeCashReceipt} onChange={e => setIncludeCashReceipt(e.target.checked)}
                style={{ width: '18px', height: '18px', accentColor: '#d97706' }} />
              <div>
                <div style={{ fontWeight: 600, color: '#92400e' }}>Készpénzbizonylat csatolása</div>
                <div style={{ fontSize: '11px', color: '#a16207', marginTop: '2px' }}>
                  A pénztárbizonylat (BPB) PDF-je is mellékelve lesz az emailben / nyomtatásnál.
                </div>
              </div>
            </label>
          </div>
        )}

      </div>

      {/* Gombok */}
      <div style={{ paddingTop: '14px', borderTop: '1px solid var(--line)', marginTop: '14px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'flex-end' }}>
          {isInvoiceType && (
            <Button
              onClick={handleSend}
              loading={sending}
              disabled={!getTargetEmail() || sent}
              style={{ background: sent ? '#16a34a' : undefined, flex: '1 1 auto', minWidth: '120px' }}
            >
              {sent ? '✓ Elküldve' : 'Kiküldés'}
              <kbd style={{ marginLeft: '6px', fontSize: '9px', opacity: 0.7, background: 'rgba(255,255,255,0.2)', borderRadius: '2px', padding: '1px 4px' }}>F6</kbd>
            </Button>
          )}
          <Button variant="ghost" onClick={handlePrint} style={{ flex: '1 1 auto', minWidth: '100px' }}>
            Nyomtatás
            <kbd style={{ marginLeft: '6px', fontSize: '9px', opacity: 0.5, background: 'var(--mist)', borderRadius: '2px', padding: '1px 4px' }}>F8</kbd>
          </Button>
          {docType === 'invoice' && (
            <Button variant="ghost" onClick={() => { onClose(); navigate(`/szamlak?open=${docId}`); }} style={{ flex: '0 0 auto' }}>
              Megnyitás
            </Button>
          )}
          <Button variant="ghost" onClick={onClose} style={{ flex: '0 0 auto' }}>Bezárás</Button>
        </div>
        <div style={{ fontSize: '10px', color: 'var(--slate)', marginTop: '8px', textAlign: 'center' }}>
          {isInvoiceType && <span>F6 Kiküldés · </span>}
          <span>F8 Nyomtatás · Esc Bezárás</span>
        </div>
      </div>
    </Modal>
    </>
  );
}
